import Debug from 'debug';
import { DfuError, ErrorCode } from '../pc-nrf-dfu-js/src/DfuError';
import DfuTransportHid from "./DfuTransportHid";
import * as slip from "../pc-nrf-dfu-js/src/util/slip";
import {report} from "process";

const debug = Debug('dfu:webhid');

const NRF_RESP_REPORT_ID = 0x5

function hidAvailable() {
    const available = "hid" in navigator;
    if (!available) {
        console.error('Sorry the WebHID API is not supported in your browser.<br>' +
            'Try enabling <span>Experimental Web Platform features</span> in <code>chrome://flags<code>.');
    }
    return available;
}


/**
 * USB HID DFU transport. Supports ULX USB devices.
 *
 * Compared to DfuTransportSerial, this transport uses the HID channel.
 * It is also instantiated with a vid/pid pair instead of a serialport instance.
 */
export default class DfuTransportWebHid extends DfuTransportHid {
    // Creates the transport. A vid and pid (integer) is required,
    // and packetReceiptNotification may also be provided (defaults
    // to 16).
    constructor(webHidDevice, onDataCallback, packetReceiveNotification = 16) {
        super(null, packetReceiveNotification);

        this.dev = webHidDevice;
        // USB_HID max size = 64. 1 byte reserved for ReportID header, 1 byte for the length
        this.mtu = 48; // NOTE: MUST be 4-byte aligned or flash writes will crash!
        this.onDataCallback = onDataCallback;
    }

    // Given a Uint8Array, sends it as the main payload / "data object".
    // Returns a Promise that resolves when payload has been transferred
    // and target has disconnected.
    sendFirmwareImage(bytes) {
        return super.sendFirmwareImage(bytes)
            .then(() => this.waitForDisconnect());
    }

    // Override the DfuTransportHid implementation,
    // since we don't want SLIP decoding
    // 'data' needs to be stripped of HID report ID and length already
    onRawData(reportId, data) {
        debug(' recv <-- [', reportId, '] ', new Uint8Array(data.buffer));


        // For talking to the nRF dongle application, we do NOT want to decode SLIP data
        // verify data came from nRF, by checking reportID
        if (this.onDataCallback) {
            this.onDataCallback(reportId, data);
            return;
        }

        // For nRF bootloader upgrades, we DO want to decode the SLIP data
        if (reportId == NRF_RESP_REPORT_ID) {
            return this.slipDecoder.decode(data);
        } else {
            console.log("Ignoring incoming data on reportId: " + reportId);
            return;
        }
    }

    // Opens the port that matches the serial number provided to the transport.
    // Returns a Promise when opening is done.
    open() {
        if (!this.dev) {
            return Promise.reject();
        }

        if (this.dev.opened) {
            return Promise.resolve();
        }

        console.log("Opening WebHID device: " + this.dev.vendorId + ":" + this.dev.productId + " " + this.dev.productName)
        return super.open();
    }

    // Waits for the target to disconnect. Times out if not
    // disconnected after 5000 ms.
    waitForDisconnect() {
        if (!this.dev || !this.dev.opened) {
            debug('dev is already closed.');
            return Promise.resolve();
        }

        // Explicit close in all cases
        return new Promise(resolve => {
            this.dev.close(() => {
                // resolve() must be delayed so macOS doesn't immediately
                // open the same device again which occurs when it is plugged in a hub,
                // and win7-x64 might also have the chance to clean up.
                setTimeout(resolve, 2000);
            });
        });
    }
}
